import { useEffect, useRef, useState } from 'react';
import { FitAddon } from '@xterm/addon-fit';
import { Terminal } from '@xterm/xterm';
import '@xterm/xterm/css/xterm.css';
import './LiveSessionTerminal.css';

import { api, type LiveSessionStart } from '../api';
import { Button } from '../ds';
import { c, cardStyle, mono } from '../tokens';
import { clipboardImageError } from './clipboardImage';

const EXIT_CONFIRM_MESSAGE = 'End this ephemeral live session? It will stop immediately and cannot be resumed later.';
const DEFAULT_WS_URL = 'ws://localhost:8080/terminal';
const DEFAULT_FONT_SIZE = 13;
const MIN_FONT_SIZE = 10;
const MAX_FONT_SIZE = 22;
const configuredTransport: SessionTransport = import.meta.env.VITE_CLAUDETTE_TERMINAL_PROTOCOL === 'gateway'
  ? 'gateway'
  : 'local';
const terminalColors = {
  background: '#111a27',
  header: '#151f2e',
  foreground: '#d3d7df',
  muted: '#9ca6b6',
  cursor: '#f3a179',
  selection: '#334155',
  accent: '#f07f5f',
};
const zoomButtonStyle = {
  border: '1px solid rgba(148, 163, 184, 0.24)',
  borderRadius: 8,
  padding: '7px 10px',
  background: '#334155',
  color: '#e2e8f0',
  font: 'inherit',
  fontSize: 12.5,
  cursor: 'pointer',
} as const;

export type SessionState = 'idle' | 'connecting' | 'connected' | 'stopping' | 'disconnected' | 'error';
type SessionTransport = 'gateway' | 'local';
type StopAction = 'kill' | 'exit-command';

interface LiveSessionTerminalProps {
  repo: string;
  active?: boolean;
  onStateChange?: (state: SessionState) => void;
}

function configuredSocketUrl(
  repo: string,
  transport: SessionTransport,
  liveSession?: LiveSessionStart,
  terminalSize?: { cols: number; rows: number },
): string {
  const base = transport === 'gateway'
    ? import.meta.env.VITE_CLAUDETTE_TERMINAL_WS_URL || DEFAULT_WS_URL
    : DEFAULT_WS_URL;
  const url = new URL(base);
  if (transport === 'gateway') return url.toString();
  url.searchParams.set('repo', repo);

  if (terminalSize) {
    url.searchParams.set('cols', String(terminalSize.cols));
    url.searchParams.set('rows', String(terminalSize.rows));
  }

  if (liveSession) {
    url.searchParams.set('liveSessionToken', liveSession.token);
    url.searchParams.set('preflightUrl', liveSession.preflightUrl);
    url.searchParams.set('workspacePath', liveSession.workspacePath);
  }

  const token = import.meta.env.VITE_CLAUDETTE_TERMINAL_TOKEN;
  if (token) url.searchParams.set('token', token);

  return url.toString();
}

function isOpeningOrOpen(socket: WebSocket): boolean {
  return socket.readyState === WebSocket.CONNECTING || socket.readyState === WebSocket.OPEN;
}

function isClosingOrClosed(socket: WebSocket): boolean {
  return socket.readyState === WebSocket.CLOSING || socket.readyState === WebSocket.CLOSED;
}

export function LiveSessionTerminal({ repo, active = true, onStateChange }: LiveSessionTerminalProps) {
  const terminalFrameRef = useRef<HTMLDivElement | null>(null);
  const terminalHostRef = useRef<HTMLDivElement | null>(null);
  const terminalRef = useRef<Terminal | null>(null);
  const fitRef = useRef<FitAddon | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const resizeFrameRef = useRef<number | null>(null);
  const lastTerminalSizeRef = useRef({ cols: 0, rows: 0 });
  const commandBufferRef = useRef('');
  const intentionalStopRef = useRef(false);
  const connectionErrorRef = useRef(false);
  const transportRef = useRef<SessionTransport>(configuredTransport);
  const sessionStateRef = useRef<SessionState>('idle');
  const startAttemptRef = useRef(0);
  const activeRef = useRef(active);
  const [sessionState, setSessionState] = useState<SessionState>('idle');
  const [sessionTransport, setSessionTransport] = useState<SessionTransport>(configuredTransport);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [fontSize, setFontSize] = useState(DEFAULT_FONT_SIZE);
  const [stopAction, setStopAction] = useState<StopAction | null>(null);

  activeRef.current = active;

  const updateSessionState = (state: SessionState): void => {
    sessionStateRef.current = state;
    setSessionState(state);
    onStateChange?.(state);
  };

  const hasLiveSession = (): boolean => {
    const socket = socketRef.current;
    return Boolean(
      socket &&
        (sessionStateRef.current === 'connecting' || sessionStateRef.current === 'connected' || sessionStateRef.current === 'stopping') &&
        isOpeningOrOpen(socket),
    );
  };

  const stopSession = (): void => {
    startAttemptRef.current += 1;
    const socket = socketRef.current;
    setStopAction(null);
    if (!socket || isClosingOrClosed(socket)) {
      updateSessionState('disconnected');
      return;
    }

    intentionalStopRef.current = true;
    updateSessionState('stopping');
    try {
      if (transportRef.current === 'local') socket.send(JSON.stringify({ type: 'kill' }));
      socket.close();
    } catch {
      socket.close();
    }
  };

  const confirmStopAction = (): void => {
    if (stopAction === 'kill') {
      stopSession();
      return;
    }

    const socket = socketRef.current;
    setStopAction(null);
    if (stopAction === 'exit-command' && socket?.readyState === WebSocket.OPEN) {
      commandBufferRef.current = '';
      socket.send(JSON.stringify({ type: 'input', data: '\r' }));
    }
    terminalRef.current?.focus();
  };

  const uploadClipboardImage = (file: Blob): void => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;

    const validationError = clipboardImageError(file);
    if (validationError) {
      terminalRef.current?.writeln(`\r\n[${validationError}]`);
      return;
    }

    const reader = new FileReader();
    reader.addEventListener('load', () => {
      if (typeof reader.result !== 'string') return;
      if (socketRef.current !== socket || socket.readyState !== WebSocket.OPEN) return;
      socket.send(JSON.stringify({ type: 'clipboard-image', dataUrl: reader.result }));
    });
    reader.addEventListener('error', () => {
      terminalRef.current?.writeln('\r\n[Clipboard image could not be read.]');
    });
    reader.readAsDataURL(file);
  };

  const uploadImageFromSystemClipboard = async (): Promise<void> => {
    try {
      const clipboardItems = await navigator.clipboard.read();
      for (const item of clipboardItems) {
        const imageType = item.types.find(type => type.startsWith('image/'));
        if (!imageType) continue;
        uploadClipboardImage(await item.getType(imageType));
        return;
      }
      terminalRef.current?.writeln('\r\n[No image found in the browser clipboard.]');
    } catch {
      terminalRef.current?.writeln('\r\n[The browser could not read the clipboard. Use Cmd+V to paste.]');
    }
  };

  const startSession = async (transport: SessionTransport): Promise<void> => {
    if (hasLiveSession()) return;
    const startAttempt = ++startAttemptRef.current;

    const terminalHost = terminalHostRef.current;
    if (!terminalHost) return;

    intentionalStopRef.current = false;
    connectionErrorRef.current = false;
    commandBufferRef.current = '';
    setStopAction(null);
    transportRef.current = transport;
    setSessionTransport(transport);
    setErrorMessage(null);
    updateSessionState('connecting');

    if (!terminalRef.current) {
      const terminal = new Terminal({
        cursorBlink: true,
        convertEol: true,
        fontFamily: `${mono}, Menlo, Monaco, Consolas, "Liberation Mono", monospace`,
        fontSize,
        theme: {
          background: terminalColors.background,
          foreground: terminalColors.foreground,
          cursor: terminalColors.cursor,
          selectionBackground: terminalColors.selection,
          black: '#0b111c',
          red: terminalColors.accent,
          green: '#8ec07c',
          yellow: '#d8b56d',
          blue: '#8aa6d6',
          magenta: '#c89bd6',
          cyan: '#7fb8c9',
          white: terminalColors.foreground,
          brightBlack: '#6f7a89',
          brightRed: '#ff8f6b',
          brightGreen: '#a7d68b',
          brightYellow: '#f0ce80',
          brightBlue: '#a9c2f0',
          brightMagenta: '#deb1ec',
          brightCyan: '#9fd2df',
          brightWhite: '#f0f3f8',
        },
      });
      const fit = new FitAddon();
      terminal.loadAddon(fit);
      terminal.open(terminalHost);
      terminal.attachCustomKeyEventHandler(event => {
        if (event.type !== 'keydown' || event.key.toLowerCase() !== 'v' || !event.ctrlKey || event.metaKey || event.altKey) {
          return true;
        }
        void uploadImageFromSystemClipboard();
        return false;
      });
      terminal.onData(data => {
        const socket = socketRef.current;
        if (socket?.readyState !== WebSocket.OPEN) return;

        if (data === '\r') {
          if (commandBufferRef.current.trim() === '/exit') {
            setStopAction('exit-command');
            return;
          }

          commandBufferRef.current = '';
          socket.send(JSON.stringify({ type: 'input', data }));
          return;
        }

        if (data === '\u007f') {
          commandBufferRef.current = commandBufferRef.current.slice(0, -1);
          socket.send(JSON.stringify({ type: 'input', data }));
          return;
        }

        if (data === '\u0003' || data === '\u0004') {
          commandBufferRef.current = '';
          socket.send(JSON.stringify({ type: 'input', data }));
          return;
        }

        if (data.length === 1 && data >= ' ') {
          commandBufferRef.current += data;
        } else if (data.includes('\r') || data.includes('\n')) {
          commandBufferRef.current = '';
        }

        socket.send(JSON.stringify({ type: 'input', data }));
      });
      terminalRef.current = terminal;
      fitRef.current = fit;
    }

    const terminal = terminalRef.current;
    const fit = fitRef.current;
    terminal.clear();
    fit?.fit();
    const initialTerminalSize = { cols: terminal.cols, rows: terminal.rows };
    terminal.focus();

    let liveSession: LiveSessionStart;
    try {
      liveSession = (await api.startLiveSession(repo)).liveSession;
    } catch (err) {
      if (startAttemptRef.current !== startAttempt) return;
      connectionErrorRef.current = true;
      setErrorMessage(err instanceof Error ? err.message : 'Could not start the live session.');
      updateSessionState('error');
      return;
    }
    if (startAttemptRef.current !== startAttempt || sessionStateRef.current !== 'connecting') return;

    const socket = new WebSocket(configuredSocketUrl(repo, transport, liveSession, initialTerminalSize));
    socketRef.current = socket;

    socket.addEventListener('open', () => {
      connectionErrorRef.current = false;
      setErrorMessage(null);
      fit?.fit();
      socket.send(JSON.stringify(transport === 'gateway'
        ? {
            type: 'authorize',
            liveSessionToken: liveSession.token,
            cols: terminal.cols,
            rows: terminal.rows,
          }
        : { type: 'resize', cols: terminal.cols, rows: terminal.rows }));
    });

    socket.addEventListener('message', event => {
      if (socketRef.current !== socket) return;
      const message = JSON.parse(event.data) as {
        type?: string;
        data?: string;
        message?: string;
        path?: string;
        exitCode?: number;
      };
      if (message.type === 'ready') {
        if (!intentionalStopRef.current) updateSessionState('connected');
        return;
      }
      if (message.type === 'error') {
        connectionErrorRef.current = true;
        setErrorMessage(message.message ?? 'Could not start the live session.');
        updateSessionState('error');
        socket.close();
        return;
      }
      if (message.type === 'exit') {
        if (message.exitCode && message.exitCode !== 0) {
          connectionErrorRef.current = true;
          setErrorMessage(`Terminal process exited with code ${message.exitCode}. Check the terminal output for the underlying error.`);
          updateSessionState('error');
        } else {
          updateSessionState('disconnected');
        }
        return;
      }
      if (message.type === 'output' && typeof message.data === 'string') {
        if (!intentionalStopRef.current && sessionStateRef.current !== 'connected') updateSessionState('connected');
        terminal.write(message.data);
        return;
      }
      if (message.type === 'clipboard-image-ready' && typeof message.path === 'string') {
        terminal.paste(message.path);
        return;
      }
      if (message.type === 'clipboard-image-error') {
        terminal.writeln(`\r\n[${message.message ?? 'Clipboard image could not be saved.'}]`);
      }
    });

    socket.addEventListener('close', () => {
      if (socketRef.current !== socket) return;
      socketRef.current = null;
      commandBufferRef.current = '';
      setStopAction(null);
      if (!connectionErrorRef.current) updateSessionState('disconnected');
      intentionalStopRef.current = false;
    });

    socket.addEventListener('error', () => {
      if (socketRef.current !== socket) return;
      connectionErrorRef.current = true;
      const socketUrl = configuredSocketUrl(repo, transport, liveSession, initialTerminalSize)
        .replace(/token=[^&]+/, 'token=redacted')
        .replace(/liveSessionToken=[^&]+/, 'liveSessionToken=redacted');
      setErrorMessage(transport === 'local'
        ? `Could not connect to ${socketUrl}. Make sure the local Claudette terminal server is running.`
        : `Could not connect to ${socketUrl}. Try starting a new pilot session.`);
      updateSessionState('error');
    });
  };

  const resizeTerminal = (): void => {
    if (!activeRef.current) return;
    if (resizeFrameRef.current !== null) window.cancelAnimationFrame(resizeFrameRef.current);
    resizeFrameRef.current = window.requestAnimationFrame(() => {
      resizeFrameRef.current = null;
      const terminal = terminalRef.current;
      const socket = socketRef.current;
      fitRef.current?.fit();
      if (!terminal || socket?.readyState !== WebSocket.OPEN) return;

      const lastSize = lastTerminalSizeRef.current;
      if (terminal.cols === lastSize.cols && terminal.rows === lastSize.rows) return;

      lastTerminalSizeRef.current = { cols: terminal.cols, rows: terminal.rows };
      socket.send(JSON.stringify({ type: 'resize', cols: terminal.cols, rows: terminal.rows }));
    });
  };

  const setTerminalFontSize = (nextFontSize: number): void => {
    const clamped = Math.min(MAX_FONT_SIZE, Math.max(MIN_FONT_SIZE, nextFontSize));
    setFontSize(clamped);
    if (terminalRef.current) {
      terminalRef.current.options.fontSize = clamped;
      window.setTimeout(resizeTerminal, 0);
    }
  };

  useEffect(() => {
    if (!active) return;

    window.setTimeout(() => {
      resizeTerminal();
      terminalRef.current?.focus();
    }, 0);
  }, [active]);

  useEffect(() => {
    const resize = (): void => resizeTerminal();

    const beforeUnload = (event: BeforeUnloadEvent): void => {
      const socket = socketRef.current;
      if (!socket || intentionalStopRef.current) return;
      if (!isOpeningOrOpen(socket)) return;

      event.preventDefault();
      event.returnValue = '';
    };

    const pageHide = (): void => {
      const socket = socketRef.current;
      if (!socket || !isOpeningOrOpen(socket)) return;

      try {
        socket.send(JSON.stringify({ type: 'kill' }));
        socket.close();
      } catch {
        /* The browser is unloading; the local server also kills on socket close. */
      }
    };

    const paste = (event: ClipboardEvent): void => {
      if (!activeRef.current) return;
      const socket = socketRef.current;
      if (!socket || socket.readyState !== WebSocket.OPEN) return;

      const items = [...(event.clipboardData?.items ?? [])];
      const imageItem = items.find(item => item.kind === 'file' && item.type.startsWith('image/'));
      const file = imageItem?.getAsFile();
      if (!file) return;

      event.preventDefault();
      event.stopPropagation();
      uploadClipboardImage(file);
    };

    window.addEventListener('resize', resize);
    window.addEventListener('beforeunload', beforeUnload);
    window.addEventListener('pagehide', pageHide);
    window.addEventListener('paste', paste, true);
    const resizeObserver = new ResizeObserver(resize);
    if (terminalFrameRef.current) resizeObserver.observe(terminalFrameRef.current);

    return () => {
      if (resizeFrameRef.current !== null) window.cancelAnimationFrame(resizeFrameRef.current);
      resizeObserver.disconnect();
      window.removeEventListener('resize', resize);
      window.removeEventListener('beforeunload', beforeUnload);
      window.removeEventListener('pagehide', pageHide);
      window.removeEventListener('paste', paste, true);
      stopSession();
      terminalRef.current?.dispose();
      terminalRef.current = null;
      fitRef.current = null;
    };
  }, []);

  const isRunning = sessionState === 'connecting' || sessionState === 'connected' || sessionState === 'stopping';
  const isTransitioning = sessionState === 'connecting' || sessionState === 'stopping';
  const showStartOverlay = sessionState !== 'connected';
  const statusColor =
    sessionState === 'connected'
      ? '#bbf7d0'
      : sessionState === 'error' || sessionState === 'disconnected'
        ? '#fecaca'
        : c.textMuted;

  return (
    <div
      className="live-session-terminal"
      style={{
        ...cardStyle,
        padding: 0,
        overflow: 'hidden',
        background: terminalColors.background,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 'calc(100vh - 220px)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          padding: '16px 18px',
          borderBottom: '1px solid rgba(148, 163, 184, 0.22)',
          background: terminalColors.header,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ color: '#eef2ff', fontSize: 16, fontWeight: 575 }}>Claudette live session</span>
          <span style={{ color: terminalColors.muted, fontSize: 12.5 }}>
            {sessionTransport === 'gateway'
              ? import.meta.env.VITE_CLAUDETTE_TERMINAL_WS_URL === 'wss://vibes.corp.abridge.com/sessions'
                ? 'Maximum session duration: 8 hours. Idle timeout: 60 minutes.'
                : 'Pilot mode enabled. Max session duration is 60 minutes.'
              : 'Local WebSocket mode enabled.'}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }} aria-label="Terminal zoom controls">
            <button
              type="button"
              disabled={fontSize <= MIN_FONT_SIZE}
              onClick={() => setTerminalFontSize(fontSize - 1)}
              onMouseEnter={event => {
                if (!event.currentTarget.disabled) event.currentTarget.style.background = '#94a3b8';
              }}
              onMouseLeave={event => {
                event.currentTarget.style.background = '#334155';
              }}
              style={{
                ...zoomButtonStyle,
                opacity: fontSize <= MIN_FONT_SIZE ? 0.45 : 1,
                cursor: fontSize <= MIN_FONT_SIZE ? 'not-allowed' : 'pointer',
              }}
            >
              -
            </button>
            <button
              type="button"
              disabled={fontSize >= MAX_FONT_SIZE}
              onClick={() => setTerminalFontSize(fontSize + 1)}
              onMouseEnter={event => {
                if (!event.currentTarget.disabled) event.currentTarget.style.background = '#94a3b8';
              }}
              onMouseLeave={event => {
                event.currentTarget.style.background = '#334155';
              }}
              style={{
                ...zoomButtonStyle,
                opacity: fontSize >= MAX_FONT_SIZE ? 0.45 : 1,
                cursor: fontSize >= MAX_FONT_SIZE ? 'not-allowed' : 'pointer',
              }}
            >
              +
            </button>
            <button
              type="button"
              disabled={fontSize === DEFAULT_FONT_SIZE}
              onClick={() => setTerminalFontSize(DEFAULT_FONT_SIZE)}
              onMouseEnter={event => {
                if (!event.currentTarget.disabled) event.currentTarget.style.background = '#94a3b8';
              }}
              onMouseLeave={event => {
                event.currentTarget.style.background = '#334155';
              }}
              style={{
                ...zoomButtonStyle,
                opacity: fontSize === DEFAULT_FONT_SIZE ? 0.45 : 1,
                cursor: fontSize === DEFAULT_FONT_SIZE ? 'not-allowed' : 'pointer',
              }}
            >
              Reset
            </button>
          </div>
          <span
            style={{
              border: '1px solid rgba(148, 163, 184, 0.28)',
              borderRadius: 999,
              padding: '7px 11px',
              color: statusColor,
              fontSize: 12.5,
              textTransform: 'capitalize',
              whiteSpace: 'nowrap',
            }}
          >
            {sessionState === 'idle' ? 'not started' : sessionState}
          </span>
          <Button variant="danger" size="sm" disabled={!isRunning || sessionState === 'stopping'} onPress={() => setStopAction('kill')}>
            {sessionState === 'stopping' ? 'Ending session...' : 'Kill session'}
          </Button>
        </div>
      </div>

      <div ref={terminalFrameRef} style={{ flex: 1, minHeight: 560, position: 'relative', overflow: 'hidden' }}>
        {showStartOverlay && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 1,
              display: 'grid',
              placeItems: 'center',
              padding: 32,
              textAlign: 'center',
              background:
                `radial-gradient(circle at top left, rgba(240, 127, 95, 0.12), transparent 32rem), ${terminalColors.background}`,
            }}
          >
            <div style={{ maxWidth: 560, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
              <span style={{ color: '#eef2ff', fontSize: 34, lineHeight: 1, fontWeight: 700, letterSpacing: '-0.04em' }}>
                Start a disposable coding session
              </span>
              <span style={{ color: '#cbd5e1', fontSize: 14.5, lineHeight: 1.5 }}>
                Launch Claudette for {repo}. This is ephemeral and will not resume after you exit or kill the session.
              </span>
              <Button
                variant="primary"
                loading={sessionState === 'connecting'}
                disabled={isTransitioning}
                onPress={() => { void startSession(configuredTransport); }}
              >
                {sessionState === 'connecting' ? 'Loading...' : sessionState === 'stopping' ? 'Ending session...' : 'Start Coding Now'}
              </Button>
              {configuredTransport === 'gateway' && (
                <button
                  type="button"
                  disabled={isTransitioning}
                  onClick={() => { void startSession('local'); }}
                  style={{
                    border: '1px solid rgba(148, 163, 184, 0.35)',
                    borderRadius: 999,
                    padding: '5px 11px',
                    background: 'rgba(51, 65, 85, 0.45)',
                    color: '#cbd5e1',
                    font: 'inherit',
                    fontSize: 12,
                    cursor: isTransitioning ? 'not-allowed' : 'pointer',
                    opacity: isTransitioning ? 0.5 : 1,
                  }}
                >
                  Use local websockets
                </button>
              )}
              {sessionState === 'error' && (
                <span style={{ color: '#fecaca', fontSize: 13 }}>
                  {errorMessage ?? 'Could not connect to the local Claudette terminal server.'}
                </span>
              )}
              {sessionState === 'disconnected' && (
                <span style={{ color: '#cbd5e1', fontSize: 13 }}>
                  Session ended. Start a new ephemeral session when you are ready.
                </span>
              )}
            </div>
          </div>
        )}
        {stopAction && (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="end-live-session-title"
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 2,
              display: 'grid',
              placeItems: 'center',
              padding: 24,
              background: 'rgba(5, 10, 18, 0.72)',
            }}
          >
            <div style={{ width: 'min(440px, 100%)', padding: 24, borderRadius: 14, border: '1px solid rgba(148, 163, 184, 0.28)', background: terminalColors.header, boxShadow: '0 24px 70px rgba(0, 0, 0, 0.45)' }}>
              <div id="end-live-session-title" style={{ color: '#eef2ff', fontSize: 18, fontWeight: 650, marginBottom: 8 }}>
                End live session?
              </div>
              <div style={{ color: '#cbd5e1', fontSize: 14, lineHeight: 1.5, marginBottom: 20 }}>
                {EXIT_CONFIRM_MESSAGE}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <Button variant="secondary" size="sm" onPress={() => { setStopAction(null); terminalRef.current?.focus(); }}>
                  Keep session
                </Button>
                <Button variant="danger" size="sm" onPress={confirmStopAction}>
                  End session
                </Button>
              </div>
            </div>
          </div>
        )}
        <div ref={terminalHostRef} style={{ position: 'absolute', inset: 0, padding: 12, boxSizing: 'border-box' }} />
      </div>
    </div>
  );
}
